'use client';

import { usePulseTracker } from '@cineamo/pulse/react';
import { useLocale, useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import FormCheckbox from '@/components/FormCheckbox';

import { WEBAPP_URL } from '@/constants/http-constants.ts';

import { addUtmParams } from '@/helpers/utm-helper.ts';
import { cn } from '@cineamo/ui/src/helpers/cn.ts';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { ButtonStyle } from '@cineamo/ui/src/button/ButtonStyle.enum';
import Button from '@cineamo/ui/src/button/NewButton';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { NewsletterTopicPreselection, useNewsletterModal } from '@/providers/NewsletterModalProvider';

import GroupedTopicToggles, { topicKey } from './GroupedTopicToggles';
import { NewsletterTopic, NewsletterTopicGroup, useNewsletterTopics } from './useNewsletterTopics';

function isValidEmail(value: string): boolean {
    if (value.includes('*')) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function extractValidationMessage(data: unknown): string | null {
    if (!data || typeof data !== 'object') return null;
    const body = data as { parameters?: Record<string, unknown>; detail?: unknown; title?: unknown };

    if (body.parameters && typeof body.parameters === 'object') {
        const messages = Object.values(body.parameters)
            .map((field) => {
                if (field && typeof field === 'object') {
                    const first = Object.values(field as Record<string, unknown>).find((v) => typeof v === 'string');
                    return typeof first === 'string' ? first : null;
                }
                return null;
            })
            .filter((m): m is string => m !== null);
        if (messages.length > 0) return messages.join(' ');
    }

    if (typeof body.detail === 'string' && body.detail !== body.title) return body.detail;
    return null;
}

/**
 * Newsletter Registration form for the standalone preferences page (Figma
 * mockup). Mirrors the modal's NewsletterSubscribeUI subscribe flow but follows
 * the cinema-website domain theme via CSS color variables (--on-background-color,
 * --primary-color, …), grouped topic toggles with per-group "All" toggles.
 */
export type NewsletterRegistrationViewProps = {
    /**
     * Which topics start switched on (CIN-13995).
     *
     * `'all'` is the standalone /newsletter entry — the footer link or a direct link — where the
     * ticket explicitly allows everything to be pre-selected. A topic array is a targeted entry (a
     * bell on a Special, Premiere or the Highlights section) and pre-selects only those, so a user
     * who arrived from one topic does not silently subscribe to all of them.
     */
    topicPreselection?: 'all' | NewsletterTopicPreselection[];
    /** Called after a successful subscribe, so the modal can close itself. */
    onSubscribed?: () => void;
};

export default function NewsletterRegistrationView({
    topicPreselection = 'all',
    onSubscribed
}: NewsletterRegistrationViewProps = {}) {
    const t = useTranslations();
    const locale = useLocale();
    const domainConfiguration = useDomainConfiguration();
    const { subscriberStatus, refreshSubscriberStatus } = useNewsletterModal();
    const { track } = usePulseTracker();
    const { allTopics } = useNewsletterTopics();

    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [desiredKeys, setDesiredKeys] = useState<Set<string>>(() => new Set());
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Seed the toggles once, from whichever entry point rendered this form.
    //
    // 'all' keeps the Figma mockup's behaviour for the standalone page. A topic array pre-selects
    // only those topics (CIN-13995): the newsletter is meant to be opt-in per topic, and a bell
    // click must not sign the user up for everything.
    //
    // Runs once after the topics list resolves — it is empty for a tick while `useShowingTags`
    // hydrates — and subsequent re-renders preserve any user de-selection.
    const hasInitialisedTopicsRef = React.useRef(false);
    useEffect(() => {
        if (hasInitialisedTopicsRef.current || allTopics.length === 0) return;
        hasInitialisedTopicsRef.current = true;

        if (topicPreselection === 'all') {
            setDesiredKeys(new Set(allTopics.map((tp) => topicKey(tp.type, tp.referenceId))));
            return;
        }

        // Intersected with allTopics so a stale or unknown preselection cannot switch on a toggle
        // that is not rendered — that would subscribe the user to something they never saw.
        const requested = new Set(topicPreselection.map((tp) => topicKey(tp.type, tp.referenceId)));
        setDesiredKeys(
            new Set(allTopics.map((tp) => topicKey(tp.type, tp.referenceId)).filter((key) => requested.has(key)))
        );
    }, [allTopics, topicPreselection]);

    // Pre-fill first/last name from a known but unconfirmed subscriber (e.g.
    // user previously subscribed but DOI is pending). Email is intentionally
    // never pre-filled — see asterisk-masking guard.
    useEffect(() => {
        if (subscriberStatus?.subscribed) {
            if (subscriberStatus.firstName) setFirstName(subscriberStatus.firstName);
            if (subscriberStatus.lastName) setLastName(subscriberStatus.lastName);
        }
    }, [subscriberStatus]);

    const toggleTopic = (topic: NewsletterTopic) => {
        const k = topicKey(topic.type, topic.referenceId);
        setDesiredKeys((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return next;
        });
    };

    const setGroup = (group: NewsletterTopicGroup, allOn: boolean) => {
        setDesiredKeys((prev) => {
            const next = new Set(prev);
            allTopics
                .filter((tp) => tp.group === group)
                .forEach((tp) => {
                    const k = topicKey(tp.type, tp.referenceId);
                    if (allOn) next.add(k);
                    else next.delete(k);
                });
            return next;
        });
    };

    const canSubmit = useMemo(
        () => Boolean(email) && isValidEmail(email) && privacyAccepted && !isSubmitting,
        [email, privacyAccepted, isSubmitting]
    );

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);

        try {
            const cinemaId = domainConfiguration.cinema?.id;
            if (!cinemaId) throw new Error('No cinema configured');

            const subscriptions: { type: string; referenceId: string | null }[] = [];
            allTopics.forEach((topic) => {
                const k = topicKey(topic.type, topic.referenceId);
                if (desiredKeys.has(k)) {
                    subscriptions.push({ type: topic.type, referenceId: topic.referenceId ?? null });
                }
            });

            const response = await fetch('/api/newsletter/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cinemaId,
                    email,
                    firstName: firstName || undefined,
                    lastName: lastName || undefined,
                    subscriptions
                })
            });

            if (response.ok) {
                refreshSubscriberStatus();
                track({ event_type: 'newsletter_subscribe' });
                showToast({
                    toastType: toast.success,
                    messageBody: t('newsletter-modal.success.message')
                });
                // Only the modal passes this; the standalone page stays put and shows the toast.
                onSubscribed?.();
            } else {
                const data = await response.json().catch(() => null);
                showToast({
                    toastType: toast.error,
                    messageTitle: t('newsletter-modal.error.title'),
                    messageBody: extractValidationMessage(data) || t('newsletter-modal.error.generic')
                });
            }
        } catch {
            showToast({
                toastType: toast.error,
                messageTitle: t('newsletter-modal.error.title'),
                messageBody: t('newsletter-modal.error.network')
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputClasses = cn(
        'h-48 px-12 rounded-4 text-body-14 bg-[rgba(var(--on-background-color-rgb),0.1)] text-[var(--on-background-color)] placeholder:text-[rgba(var(--on-background-color-rgb),0.4)] border border-[rgba(var(--on-background-color-rgb),0.3)] transition-colors',
        'border rounded-8 transition-colors focus:outline-none focus:border-transparent focus:ring-2 focus:ring-inset focus:ring-[var(--primary-color)]'
    );

    return (
        <div className="flex flex-col gap-24 py-32 max-w-[820px] mx-auto px-16 text-[var(--on-background-color)]">
            <h1 className="text-headline-24">{t('newsletter-preferences.registration.title')}</h1>

            <div className="flex flex-col gap-16">
                <div className="flex flex-col gap-4">
                    <label
                        htmlFor="registration-email"
                        className="text-body-12 text-[rgba(var(--on-background-color-rgb),0.5)]">
                        {t('newsletter-preferences.registration.email')} *
                    </label>
                    <input
                        id="registration-email"
                        type="email"
                        placeholder="example@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={inputClasses}
                        required
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                    <div className="flex flex-col gap-4">
                        <label
                            htmlFor="registration-first-name"
                            className="text-body-12 text-[rgba(var(--on-background-color-rgb),0.5)]">
                            {t('newsletter-preferences.registration.first-name')}
                        </label>
                        <input
                            id="registration-first-name"
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className={inputClasses}
                        />
                    </div>
                    <div className="flex flex-col gap-4">
                        <label
                            htmlFor="registration-last-name"
                            className="text-body-12 text-[rgba(var(--on-background-color-rgb),0.5)]">
                            {t('newsletter-preferences.registration.last-name')}
                        </label>
                        <input
                            id="registration-last-name"
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className={inputClasses}
                        />
                    </div>
                </div>
            </div>

            <h2 className="text-body-bold-14 mt-8">{t('newsletter-preferences.registration.interests')}</h2>

            <GroupedTopicToggles
                desiredKeys={desiredKeys}
                onToggleTopic={toggleTopic}
                onSetGroup={setGroup}
            />

            <div className="flex flex-col gap-8">
                <FormCheckbox
                    name="registration-privacy"
                    on="background"
                    checked={privacyAccepted}
                    onChange={setPrivacyAccepted}
                    required
                    label={t.rich('newsletter-preferences.registration.privacy-consent', {
                        privacy: (chunks) => (
                            <a
                                href={addUtmParams(`${WEBAPP_URL}/data-protection?locale=${locale}`, {
                                    utmSource: 'cinema_website',
                                    utmContent: 'newsletter-preferences-data_protection',
                                    utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                                    utmMedium: 'website'
                                })}
                                className="underline text-[var(--primary-color)]"
                                target="_blank"
                                rel="noopener noreferrer">
                                {chunks}
                            </a>
                        )
                    })}
                />

                <p className="text-body-12 text-[rgba(var(--on-background-color-rgb),0.5)]">
                    {t('newsletter-preferences.registration.required-note')}
                </p>

                <Button
                    className="self-start"
                    style={ButtonStyle.default}
                    onClick={handleSubmit}
                    disabled={!canSubmit}>
                    {isSubmitting
                        ? t('newsletter-preferences.registration.submitting')
                        : t('newsletter-preferences.registration.submit')}
                </Button>
            </div>
        </div>
    );
}
